/* Client-side dismissal of notifications */
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'src/store/store';
import { isDismissible, XptNotification } from './notificationModels';
import { notificationDismissed } from './notificationsSlice';

/**
 * Hides notifications the user dismissed. Per-browser, as there is no backend delete.
 *
 * The ids live in the store because both the bell and the panel call this hook; component state
 * gave each its own copy, leaving the bell's badge counting items dismissed in the panel.
 */
export const useDismissedNotifications = (items: XptNotification[]) => {
  const dispatch = useDispatch<AppDispatch>();
  const dismissedIds = useSelector((state: RootState) => state.notificationsStore.dismissedIds);

  const dismiss = useCallback(
    (notificationId: string) => {
      dispatch(notificationDismissed(notificationId));
    },
    [dispatch]
  );

  const visibleItems = useMemo(() => {
    if (dismissedIds.length === 0) {
      return items;
    }
    const dismissed = new Set(dismissedIds);
    // Never hide a pending item, even if its id is in the stored list.
    return items.filter((item) => !dismissed.has(item.notification_id) || !isDismissible(item));
  }, [items, dismissedIds]);

  return { visibleItems, dismiss };
};
